import React from 'react';
import dateFormat from 'dateformat';
import { startOfMinute, endOfMinute } from 'date-fns';
import { locationsSelector, eventsSelector } from '#selector/shipment/overview';
import { updateBreadcrumb } from '#action/breadcrumb';
import { MapWithPreloader as Map } from '#component/map';
import { WARNING } from '#action/types';
import DefaultPreloader from '#component/preloader';
import { getContrastColor } from '#component/theme';
import { getChartDateRange, getPeriod, getChartRange } from './helper';
import DesktopView from './components/desktop-view';
import MobileView from './components/mobile-view';
import Header from './components/header';
import propTypes from './prop-types';
import { OverviewContainer } from './styles';
import { setDocumentTitle, isInMonthRange } from '#helper';

const MAP_TAB = 90;
const EVENTS_TAB = 100;

const isWidthDown = (theme, breakpoint, width) =>
  theme.breakpoints.values[width] < theme.breakpoints.values[breakpoint];

export default class DeviceOverview extends React.Component {
  constructor(props) {
    super(props);
    const { tabNum = 90, startDate, endDate } = props.match.params;

    let date2;
    let date1;
    let isDateSetFromUrl = false;

    if (!Number.isNaN(Date.parse(endDate)) && !Number.isNaN(Date.parse(startDate))) {
      date2 = new Date(endDate);
      date1 = new Date(startDate);
      isDateSetFromUrl = true;
    } else {
      date2 = new Date();
      date1 = new Date(date2 - 24 * 60 * 60 * 1000);
    }

    if (!isDateSetFromUrl) {
      this.replaceHistoryParams({
        tabNum,
        startDate: dateFormat(date1, 'isoUtcDateTime'),
        endDate: dateFormat(date2, 'isoUtcDateTime')
      });
    }

    this.state = {
      rangeInterval: Math.round((date2 - date1) / 8 / 1000),
      isDateSetFromUrl,
      deviceLoading: true,
      eventsLoading: true,
      identifiersLoading: true,
      identifiers: [],
      isInRange: isInMonthRange(date1, date2, 3),
      clickedDevices: [],
      clickedEvents: [],
      tabSelected: 2,
      openEndDateDialog: false
    };
  }

  componentDidMount() {
    this.fetchData();
    this.updateBreadcrumbs();

    while (document.getElementsByClassName('d3-tip').length) {
      document.body.removeChild(document.getElementsByClassName('d3-tip')[0]);
    }
  }

  componentDidUpdate(prevProps) {
    const { width, match: { params: { tabNum } }, device, theme } = this.props;
    if (tabNum === `${EVENTS_TAB}` && !isWidthDown(theme, 'md', width)) {
      this.replaceHistoryParams({ tabNum: '90' });
    }

    if (device.title !== prevProps.device.title || device.uid !== prevProps.device.uid) {
      this.updateBreadcrumbs();
    }
  }

  componentWillUnmount() {
    const { clearMetrics, clearDevice } = this.props;
    clearMetrics();
    clearDevice();
  }

  fetchData = async () => {
    const {
      match: { params },
      fetchMeta,
      fetchDevice
    } = this.props;
    const { deviceId, startDate, endDate } = params;
    const { rangeInterval, isDateSetFromUrl } = this.state;

    const [deviceResponse] = await Promise.all([
      fetchDevice({ deviceId }),
      fetchMeta()
    ]);

    if (deviceResponse.errorMessage) {
      return;
    }

    const {
      meta,
      device,
      device: {
        project: { beginDate, expirationDate } = {},
        lastMetric = { timestamp: 0 },
        lastEvent = { timestamp: 0 },
        lastLocation = { timestamp: 0 },
        events,
        title,
        uid
      }
    } = this.props;

    setDocumentTitle('Device dashboard', title || uid);

    const identifiers = meta.device
      ?.find(m => m.name === device.type)?.metrics
      ?.map(m => m.identifier) ?? [];
    const loadings = identifiers.reduce((a, c) => ({ ...a, [`${c}Loading`]: true }), {});
    this.setState({
      deviceLoading: false,
      identifiersLoading: false,
      identifiers,
      ...loadings,
      locationsLoading: true
    });

    const lastDeviceLog = events && events[0];

    const period = getPeriod({
      lastMetric,
      lastEvent,
      lastDeviceLog,
      lastLocation,
      startDate,
      endDate,
      rangeInterval,
      beginDate,
      expirationDate,
      isDateSetFromUrl
    });

    this.replaceHistoryParams({
      startDate: period.start,
      endDate: period.end
    });

    if (this.state.isInRange) {
      await this.fetchTabsData(identifiers, period);
    }
  };

  replaceHistoryParams = newParams => {
    const {
      history,
      match: { params: currentParams }
    } = this.props;
    const { deviceId, tabNum, startDate, endDate } = { ...currentParams, ...newParams };
    history.replace(`/device/${deviceId}/t/${tabNum}/dr/${startDate}/${endDate}`);
  };

  setDateRange = async (rangeStart, rangeEnd) => {
    const startDate = dateFormat(startOfMinute(new Date(rangeStart)), 'isoUtcDateTime');
    const endDate = dateFormat(endOfMinute(new Date(rangeEnd)), 'isoUtcDateTime');

    this.replaceHistoryParams({ startDate, endDate });
    const date1 = new Date(startDate);
    const date2 = new Date(endDate);

    await this.setState({ isInRange: isInMonthRange(date1, date2, 3) });

    if (this.state.isInRange) {
      const { identifiers } = this.state;
      const loadings = identifiers.reduce((a, c) => ({ ...a, [`${c}Loading`]: true }), {});

      this.setState({ ...loadings, locationsLoading: true });
      await this.fetchTabsData(
        identifiers,
        { start: startDate, end: endDate }
      );
    }
  };

  setOpenEndDateDialog = async value => this.setState({ openEndDateDialog: value });

  updateBreadcrumbs = () => {
    const { device } = this.props;

    if (device.title && device.uid) {
      this.props.dispatch(
        updateBreadcrumb([
          { label: 'Devices', path: '/device' },
          { label: device.title || device.uid }
        ])
      );
    }
  };

  setChart = chart => {
    this.chart = chart;
  };

  setMap = map => {
    this.map = map;
  };

  getDateData = device => {
    const minMaxDate = {};

    if (device.project && device.project.beginDate) {
      minMaxDate.minDate = startOfMinute(new Date(device.project.beginDate));

      const opt = {};
      opt.beginDate = device.project.beginDate;

      if (device.project.expirationDate) {
        minMaxDate.maxDate = endOfMinute(new Date(device.project.expirationDate));
        opt.expirationDate = device.project.expirationDate;
      }
    }

    return minMaxDate;
  };

  handleEventHover = (event, item) => {
    if (this.chart) {
      this.chart.highlightTimestamp = event.type === 'mouseenter' ? new Date(item.timestamp) : null;
      this.chart.highlightEvent(this.chart.highlightTimestamp);
    }
  };

  handleEventGroupHover = (event, item) => {
    if (this.chart) {
      this.chart.highlightTimestamp = event.type === 'mouseenter' ? getChartRange(item) : null;
      this.chart.highlightEventGroup(this.chart.highlightTimestamp);
    }
  };

  handleEventClick = (event, item) => {
    const { dispatch, match: { params: { tabNum } } } = this.props;
    const tab = tabNum ? parseInt(tabNum, 10) : 90;

    if (tab !== MAP_TAB) {
      if (this.chart) {
        if (Array.isArray(item)) {
          this.chart.highlightTimestamp = getChartRange(item);
          this.chart.highlightEventGroup(this.chart.highlightTimestamp);
        }
        this.chart.moveChart(getChartRange(item));
      }
    } else if (!item.latitude || !item.longitude) {
      dispatch({
        type: WARNING,
        payload: {
          message: "Event doesn't have coordinates"
        }
      });
    } else if (this.map) {
      this.map.app.clickCheckpoint(item.latitude, item.longitude);
    }
  };

  handleEventGroupClosing = (event, item) => {
    const { match: { params: { tabNum } } } = this.props;
    const tab = tabNum ? parseInt(tabNum, 10) : 90;

    if (tab !== MAP_TAB && this.chart) {
      this.chart.resetScale();
      this.chart.highlightTimestamp = getChartRange(item);
      this.chart.highlightEventGroup(this.chart.highlightTimestamp);
    }
  };

  setClickedDevices = clickedDevices => {
    // "Escape"
    const v = Array.isArray(clickedDevices) ? clickedDevices : [];
    this.setState({ clickedDevices: v });
  };

  setClickedEvents = events => {
    // "Escape"
    // const e = Array.isArray(events) ? events : [];
    this.setState({ clickedEvents: events });
  };

  handleMeasurementClick = clickedDevices => {
    const clickedAxises = clickedDevices?.map(cd => cd.axis);

    this.setState({ selectedAxis: clickedAxises });
  };

  setSelectedAxis = axis => {
    this.setState({ selectedAxis: axis });
  };

  async fetchTabsData(identifiers = [], period = {}) {
    const {
      match: { params },
      fetchLocations,
      fetchEvents,
      fetchDeviceLogs,
      fetchMetricsByIdentifier
    } = this.props;

    const { deviceId, tabNum } = params;
    const tab = tabNum ? parseInt(tabNum, 10) : 90;

    let firstTabRequest;
    const primaryRequests = [
      { fn: fetchEvents, args: [{ deviceId }, period] }
    ];
    const restTabsRequests = [
      ...identifiers.map(identifier =>
        ({ fn: fetchMetricsByIdentifier,
          args: [{ deviceId }, period, identifier],
          identifier })),
      { fn: fetchLocations, args: [{ deviceId }, period], identifier: 'locations' },
      { fn: fetchDeviceLogs, args: [{ deviceId }, period] }
    ];

    if (identifiers[tab]) {
      [firstTabRequest] = restTabsRequests.splice(tab, 1);
    } else if (tab === MAP_TAB) {
      firstTabRequest = restTabsRequests.pop();
    } else {
      firstTabRequest = restTabsRequests.shift();
    }
    primaryRequests.unshift(firstTabRequest);

    const requestCallback = identifier => () => {
      if (identifier) {
        const state = {};
        state[`${identifier}Loading`] = false;
        this.setState(state);
      }
    };

    await Promise.all([
      ...primaryRequests.map(pr => pr.fn.call(this, ...pr.args)
        .then(requestCallback(pr.identifier)))
    ]).then(() => this.setState({
      eventsLoading: false
    }));
    await Promise.all([
      ...restTabsRequests.map(rtr => rtr.fn.call(this, ...rtr.args)
        .then(requestCallback(rtr.identifier)))
    ]);
  }

  render() {
    const {
      classes,
      device,
      metrics,
      locations,
      width,
      match: { params: { tabNum, startDate, endDate } },
      theme,
      account
    } = this.props;

    const {
      deviceLoading,
      eventsLoading,
      identifiersLoading,
      identifiers,
      isInRange,
      clickedDevices,
      clickedEvents,
      selectedAxis
    } = this.state;

    const isWidthDownMd = isWidthDown(theme, 'md', width);
    const isWidthDownSm = isWidthDown(theme, 'sm', width);

    const tab = tabNum && !(tabNum === `${EVENTS_TAB}` && !isWidthDownMd) ? parseInt(tabNum, 10) : 90;

    const rangeStart = startOfMinute(new Date(startDate));
    const rangeEnd = endOfMinute(new Date(endDate));

    const chartDateRange = getChartDateRange(rangeStart, rangeEnd);

    const minMaxDate = this.getDateData(device);

    const color = getContrastColor();

    const devices = [{
      ...device,
      color: color.slice(1),
      locations,
      metrics,
      selectedAxis
    }];
    const selectedLocations = locationsSelector(devices);
    const selectedEvents = eventsSelector(devices);

    const MapComponent = <Map
      loading={this.state.locationsLoading}
      locations={selectedLocations}
      isMobile={isWidthDownSm}
      events={selectedEvents}
      className={classes.map}
      setMap={this.setMap}
      devices={devices}
      clickDevices={this.setClickedDevices}
      mapClass="deviceOverviewPage"
    />;

    const sidebarComponentsProps = {
      events: selectedEvents,
      eventsLoading: false,
      isMobile: isWidthDownMd,
      identifiersMap: {
        2: 'events'
      },
      tabSelected: this.state.tabSelected,
      setTabSelected: t => this.setState({ tabSelected: t }),
      handleEventGroupHover: this.handleEventGroupHover,
      handleEventGroupClosing: this.handleEventGroupClosing
    };

    const sidebarProps = {
      handleEventClick: this.handleEventClick,
      handleEventHover: this.handleEventHover
    };

    const sidebarSharedProps = {
      clickedDevices,
      clickedEvents,
      setClickedDevices: this.setClickedDevices,
      setClickedEvents: this.setClickedEvents,
      setSelectedAxis: this.setSelectedAxis
    };

    const deviceOverviewProps = {
      parentState: this.state,
      isInRange,
      chartDateRange,
      identifiers,
      devices,
      tab,
      setChart: this.setChart,
      events: selectedEvents,
      MapComponent,
      match: this.props.match
    };

    if (deviceLoading || (isInRange && eventsLoading)) {
      return <DefaultPreloader />;
    }

    return (
      <OverviewContainer className="cluster overview">
        <Header
          isInRange={isInRange}
          isWidthDownSm={isWidthDownSm}
          minMaxDate={minMaxDate}
          rangeStart={rangeStart}
          rangeEnd={rangeEnd}
          setDateRange={this.setDateRange}
          device={device}
          metrics={metrics}
          account={account}
          setOpenEndDateDialog={this.setOpenEndDateDialog}
          openEndDateDialog={this.state.openEndDateDialog}
        />
        {!isWidthDownSm ? (
          <DesktopView
            identifiersLoading={identifiersLoading}
            isWidthDownMd={isWidthDownMd}
            sidebarProps={sidebarProps}
            sidebarSharedProps={sidebarSharedProps}
            sidebarComponentsProps={sidebarComponentsProps}
            handleMeasurementClick={this.handleMeasurementClick}
            {...deviceOverviewProps}
          />
        ) :
          <MobileView
            replaceHistoryParams={this.replaceHistoryParams}
            locations={locations}
            locationsLoading={this.state.locationsLoading}
            clickedDevices={clickedDevices}
            device={device}
            metrics={metrics}
            meta={this.props.meta}
            {...deviceOverviewProps}
            {...sidebarSharedProps}
          />}
      </OverviewContainer>
    );
  }
}

DeviceOverview.propTypes = propTypes;
